import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { EditorShell } from "./EditorShell";
import { isAnswerChanged, useEditorDraft } from "./editorValues";
import {
  physicianAnswerFromDraft,
  physicianDraftProblem,
  readPhysicianAnswer,
  type PhysicianDraft,
} from "./physicianValue";
import type { QuestionEditorProps } from "./types";

// Editor of RNPlatformPatientPhysician: the physician details mobile fills from its physician
// search (name, address, NPI, phone numbers with institution, fax numbers), as a plain form. Save
// writes the `RNPlatformPatientPhysicianDetails` object (physicianValue.ts); an emptied form
// removes the answer.

function ListSection(props: {
  addLabel: string;
  children: React.ReactNode;
  isEmpty: boolean;
  label: string;
  onAdd: () => void;
}) {
  return (
    <Stack spacing={2}>
      <Text fontSize="sm" fontWeight={500}>
        {props.label}
      </Text>
      {props.isEmpty && (
        <Text color="gray.500" fontSize="xs">
          None
        </Text>
      )}
      {props.children}
      <Button
        alignSelf="flex-start"
        leftIcon={<FiPlus />}
        size="sm"
        variant="ghost"
        onClick={props.onAdd}
      >
        {props.addLabel}
      </Button>
    </Stack>
  );
}

export function RNPlatformPhysicianEditor(props: QuestionEditorProps) {
  const stored = readPhysicianAnswer(props.value);
  const { draft, isTouched, updateDraft } = useEditorDraft<PhysicianDraft>(() => stored.draft);
  const next = physicianAnswerFromDraft(draft);
  const problem = physicianDraftProblem(draft);

  const setField = (field: "address" | "name" | "npi", value: string) =>
    updateDraft((prev) => ({ ...prev, [field]: value }));
  const setPhone = (index: number, field: "institution" | "number", value: string) =>
    updateDraft((prev) => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.map((phone, i) =>
        i === index ? { ...phone, [field]: value } : phone
      ),
    }));
  const setFax = (index: number, value: string) =>
    updateDraft((prev) => ({
      ...prev,
      faxNumbers: prev.faxNumbers.map((fax, i) => (i === index ? value : fax)),
    }));

  return (
    <EditorShell
      canSave={isTouched && problem === null && isAnswerChanged(props.value, next)}
      error={props.error}
      isSaving={props.isSaving}
      label={props.question.label || "Patient Physician"}
      onCancel={props.onCancel}
      onSave={() => props.onSave(next)}
    >
      <Stack spacing={4}>
        {!stored.isReadable && !isTouched && (
          <Text color="orange.600" fontSize="xs">
            The stored answer could not be read as physician details. It is kept unless you fill the
            form.
          </Text>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="sm" mb={1}>
              Name
            </FormLabel>
            <Input
              autoFocus={true}
              size="sm"
              value={draft.name}
              onChange={(event) => setField("name", event.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" mb={1}>
              NPI
            </FormLabel>
            <Input
              size="sm"
              value={draft.npi}
              onChange={(event) => setField("npi", event.target.value)}
            />
          </FormControl>
        </SimpleGrid>

        <FormControl>
          <FormLabel fontSize="sm" mb={1}>
            Address
          </FormLabel>
          <Input
            size="sm"
            value={draft.address}
            onChange={(event) => setField("address", event.target.value)}
          />
        </FormControl>

        <ListSection
          addLabel="Add phone number"
          isEmpty={draft.phoneNumbers.length === 0}
          label="Phone numbers"
          onAdd={() =>
            updateDraft((prev) => ({
              ...prev,
              phoneNumbers: [...prev.phoneNumbers, { number: "", institution: "" }],
            }))
          }
        >
          {draft.phoneNumbers.map((phone, index) => (
            <Flex key={index} alignItems="center" gap={2}>
              <Input
                aria-label={`Phone number ${index + 1}`}
                flex={1}
                placeholder="Number"
                size="sm"
                value={phone.number}
                onChange={(event) => setPhone(index, "number", event.target.value)}
              />
              <Input
                aria-label={`Institution ${index + 1}`}
                flex={2}
                placeholder="Institution"
                size="sm"
                value={phone.institution}
                onChange={(event) => setPhone(index, "institution", event.target.value)}
              />
              <IconButton
                aria-label={`Remove phone number ${index + 1}`}
                icon={<FiTrash2 />}
                size="sm"
                variant="ghost"
                onClick={() =>
                  updateDraft((prev) => ({
                    ...prev,
                    phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== index),
                  }))
                }
              />
            </Flex>
          ))}
        </ListSection>

        <ListSection
          addLabel="Add fax number"
          isEmpty={draft.faxNumbers.length === 0}
          label="Fax numbers"
          onAdd={() => updateDraft((prev) => ({ ...prev, faxNumbers: [...prev.faxNumbers, ""] }))}
        >
          {draft.faxNumbers.map((fax, index) => (
            <Flex key={index} alignItems="center" gap={2}>
              <Input
                aria-label={`Fax number ${index + 1}`}
                flex={1}
                placeholder="Fax number"
                size="sm"
                value={fax}
                onChange={(event) => setFax(index, event.target.value)}
              />
              <IconButton
                aria-label={`Remove fax number ${index + 1}`}
                icon={<FiTrash2 />}
                size="sm"
                variant="ghost"
                onClick={() =>
                  updateDraft((prev) => ({
                    ...prev,
                    faxNumbers: prev.faxNumbers.filter((_, i) => i !== index),
                  }))
                }
              />
            </Flex>
          ))}
        </ListSection>

        {isTouched && problem !== null && (
          <Text color="gray.600" fontSize="xs">
            {problem}
          </Text>
        )}
        {isTouched && next === null && isAnswerChanged(props.value, next) && (
          <Text color="orange.600" fontSize="xs">
            Saving an empty form removes the physician from this document.
          </Text>
        )}
      </Stack>
    </EditorShell>
  );
}
